import type { EnvironmentDomain, TopologyWebsiteView } from '../api';

/**
 * One reading of storefront health, shared by the website rows and the
 * hostname table of the storefront map (CO-87).
 *
 * A website's own health comes from the continuous site checks; its
 * hostnames carry their own observations (DNS, HTTPS certificate, the
 * verification request). Shown separately, the two contradicted each other:
 * a website "Not checked yet" above its own hostname "Certificate expired".
 * The website's status is therefore the worse of the two readings, and
 * "Not checked yet" only when neither has produced anything.
 */
export type SiteStatus = 'healthy' | 'warning' | 'critical' | 'unknown' | 'disabled';

const badTls = new Set(['invalid', 'expired', 'hostname_mismatch', 'missing']);
const badDns = new Set(['missing', 'mismatch']);

/** What the observations of one hostname add up to. */
export function domainStatus(domain: EnvironmentDomain): SiteStatus {
  if (badDns.has(domain.dns_status) || badTls.has(domain.tls_observed_status) || domain.verification_status === 'failed') {
    return 'critical';
  }
  if (domain.verification_status === 'warning') return 'warning';
  // 'proxied': DNS answers a CDN edge that forwards to this environment's gate (CO-122).
  const dnsOk = domain.dns_status === 'ok' || domain.dns_status === 'proxied';
  const tlsOk = domain.tls_observed_status === 'valid' || domain.tls_observed_status === 'not_applicable';
  if (domain.verification_status === 'passed' || (dnsOk && tlsOk)) return 'healthy';
  return 'unknown';
}

/** The problems of one hostname, in words, or [] when it has none. */
export function domainProblems(domain: EnvironmentDomain, labels: { dns: (v: string) => string; tls: (v: string) => string }): string[] {
  const parts: string[] = [];
  if (badDns.has(domain.dns_status)) parts.push(labels.dns(domain.dns_status));
  if (badTls.has(domain.tls_observed_status)) parts.push(labels.tls(domain.tls_observed_status));
  if (parts.length === 0 && domain.verification_status === 'failed') parts.push(domain.verification_error || 'Failing');
  return parts;
}

const rank: Record<SiteStatus, number> = { unknown: 0, disabled: 0, healthy: 1, warning: 2, critical: 3 };

/** The worse of two readings; "unknown" never outranks a real reading. */
export function worseStatus(a: SiteStatus, b: SiteStatus): SiteStatus {
  return rank[b] > rank[a] ? b : a;
}

/** A website's status: its site checks combined with its hostnames. */
export function websiteStatus(website: TopologyWebsiteView): SiteStatus {
  if (website.website.status === 'removed') return 'critical';
  const own = (website.health?.status ?? 'unknown') as SiteStatus;
  if (own === 'disabled') return own;
  return website.domains.map(domainStatus).reduce(worseStatus, own);
}
